/**
 * Service for managing church members through the ConnectPath API.
 * 
 * This service provides real API implementations for member management functionality
 * including member profiles, family relationships, group assignments, and activities.
 * It communicates with backend endpoints to manage member-related data.
 * 
 * This service provides methods for:
 * - Creating, reading, updating, and deleting member records
 * - Managing family relationships and households
 * - Assigning members to groups and ministries
 * - Tracking member activities and engagement
 * - Bulk operations and data imports
 * 
 * @example
 * const memberService = new ApiMemberService('/api/v1');
 * const members = await memberService.getMembers({ status: 'active' });
 */
import { BaseService } from '../base/BaseService';
import { IMemberService } from '../../interfaces/IMemberService';
import { 
  Member, 
  Group,
  Family,
  MemberFilter,
  MemberImportData,
  BulkOperation,
  MemberActivity,
  MemberStats,
  MemberGroup
} from '../../../types/member';
import { enhancedTokenManager as tokenManager } from '../../EnhancedTokenManager';
import { authStateManager } from '../../../utils/authStateManager';

export class ApiMemberService extends BaseService implements IMemberService {
  private apiUrl: string;

  constructor(apiUrl: string) {
    super('ApiMemberService');
    this.apiUrl = apiUrl;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await tokenManager.getToken();
    const user = authStateManager.getUser();
    const selectedChurchId = user?.churchId || user?.church_id ||
                             user?.preferences?.selectedChurchId ||
                             localStorage.getItem('selectedChurchId');

    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(selectedChurchId ? { 'X-Selected-Church-ID': selectedChurchId } : {})
    };
  }

  /**
   * Transform API member response to match Member interface
   */
  private transformApiMember(apiMember: any): Member {
    // Our new API returns members with first_name and last_name directly
    // Map ALL backend fields to frontend Member interface
    return {
      id: apiMember.id,
      firstName: apiMember.first_name || '',
      lastName: apiMember.last_name || '',
      email: apiMember.email || '',
      phone: apiMember.phone || '',
      // Backend field is birth_date, NOT date_of_birth
      dateOfBirth: apiMember.birth_date,
      // Gender and marital status (SSOT from /choices/ endpoints)
      gender: apiMember.gender || undefined,
      maritalStatus: apiMember.marital_status || undefined,
      membershipStatus: apiMember.membership_status || 'active',
      membershipDate: apiMember.member_since,
      role: 'member', // Members are not users, they don't have system roles
      ministries: apiMember.ministries || [],
      groups: [], // Groups not implemented in new API yet
      // SSOT flat address fields (preferred by ContactInfoTab)
      addressLine1: apiMember.address_line1 || '',
      addressLine2: apiMember.address_line2 || '',
      city: apiMember.city || '',
      state: apiMember.state || '',
      postalCode: apiMember.postal_code || '',
      // Legacy nested address object (for backwards compatibility)
      address: {
        street: apiMember.address_line1 || '',
        street1: apiMember.address_line1 || '',
        street2: apiMember.address_line2 || '',
        city: apiMember.city || '',
        state: apiMember.state || '',
        postalCode: apiMember.postal_code || '',
        zipCode: apiMember.postal_code || ''
      },
      joinedDate: apiMember.member_since,
      notes: '',
      createdAt: apiMember.created_at,
      updatedAt: apiMember.updated_at,
      // Group leadership eligibility
      isGroupLeader: apiMember.is_group_leader || false,
      // Resignation fields
      resignationDate: apiMember.resignation_date,
      resignationReason: apiMember.resignation_reason,
      // Profile picture
      profilePictureUrl: apiMember.profile_picture_url || undefined,
      // Members don't have user associations
      userId: undefined,
      canViewDirectory: false,
      canManageEvents: false,
      canManageMembers: false
    } as Member;
  }

  /**
   * Get a paginated list of members from the API with optional filters.
   * 
   * @param {MemberFilter} filter - Optional filters to apply
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} Paginated member list with metadata
   * @throws {Error} If the API request fails
   */
  async getMembers(filter?: MemberFilter, page: number = 1, limit: number = 20): Promise<{
    members: Member[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    
    if (filter) {
      if (filter.search) params.append('search', filter.search);
      if (filter.status) params.append('status', filter.status);
      if (filter.groupIds?.length) params.append('groups', filter.groupIds.join(','));
      if (filter.ministryIds?.length) params.append('ministries', filter.ministryIds.join(','));
      if (filter.joinedAfter) params.append('joined_after', filter.joinedAfter);
      if (filter.joinedBefore) params.append('joined_before', filter.joinedBefore);
    }

    // Use the new members endpoint that handles church filtering on the backend
    const url = `/api/v1/members/?${params}`;
    console.log('Fetching members from:', url);
    
    const response = await fetch(url, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch members: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle response - API returns array directly
    const rawMembers = Array.isArray(data) ? data : (data.results || []);
    const total = Array.isArray(data) ? data.length : (data.count || rawMembers.length);
    
    // Transform API response to match Member interface
    const members = rawMembers.map((apiMember: any) => this.transformApiMember(apiMember));
    
    return {
      members: members,
      total: total,
      page: page,
      totalPages: Math.ceil(total / limit)
    };
  }

  async getMemberById(id: string): Promise<Member> {
    // Use the new member detail endpoint
    const response = await fetch(`/api/v1/members/${id}/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Member not found');
      }
      throw new Error(`Failed to fetch member: ${response.statusText}`);
    }

    const apiMember = await response.json();
    return this.transformApiMember(apiMember);
  }

  async createMember(data: Omit<Member, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<Member> {
    // Note: church is set from request context by ChurchIsolationMixin
    // Transform to API format - only include fields the backend accepts
    const apiData: Record<string, any> = {
      first_name: data.firstName,
      last_name: data.lastName,
      membership_status: data.membershipStatus || 'active',
    };

    // Optional fields - only include if provided
    if (data.email) apiData.email = data.email;
    if (data.phone) apiData.phone = data.phone;
    if (data.membershipDate) apiData.member_since = data.membershipDate;
    if (data.dateOfBirth) apiData.birth_date = data.dateOfBirth;
    if (data.ministries && data.ministries.length > 0) {
      apiData.ministries = data.ministries;
    }

    const response = await fetch(`/api/v1/members/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(apiData)
    });

    if (!response.ok) {
      const error = await response.json();
      // Handle validation errors from DRF
      if (typeof error === 'object' && !error.error && !error.detail) {
        // DRF returns field-specific errors like { "field_name": ["error message"] }
        const errorMessages = Object.entries(error)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('; ');
        throw new Error(errorMessages || 'Failed to create member');
      }
      throw new Error(error.error || error.detail || 'Failed to create member');
    }

    const result = await response.json();
    // Fetch the created member to get full details
    return this.getMemberById(result.id);
  }

  async updateMember(id: string, updates: Partial<Member>): Promise<Member> {
    // Transform updates to API format - only include fields that are provided
    // This ensures we only send changed fields, not blanket saves
    const apiUpdates: any = {};

    // Basic fields
    if (updates.firstName !== undefined) apiUpdates.first_name = updates.firstName;
    if (updates.lastName !== undefined) apiUpdates.last_name = updates.lastName;
    if (updates.email !== undefined) apiUpdates.email = updates.email;
    if (updates.phone !== undefined) apiUpdates.phone = updates.phone;

    // Membership fields
    if (updates.membershipStatus !== undefined) apiUpdates.membership_status = updates.membershipStatus;
    if (updates.membershipDate !== undefined) apiUpdates.member_since = updates.membershipDate;
    if (updates.ministries !== undefined) apiUpdates.ministries = updates.ministries;

    // Date of birth - backend field is birth_date, NOT date_of_birth
    // Backend expects YYYY-MM-DD format string
    if (updates.dateOfBirth !== undefined) {
      if (updates.dateOfBirth instanceof Date) {
        // Convert Date object to YYYY-MM-DD string
        apiUpdates.birth_date = updates.dateOfBirth.toISOString().split('T')[0];
      } else if (typeof updates.dateOfBirth === 'string' && updates.dateOfBirth) {
        // If it's already a string, ensure it's in YYYY-MM-DD format
        // Handle ISO date strings like "2025-12-18T00:00:00.000Z"
        if (updates.dateOfBirth.includes('T')) {
          apiUpdates.birth_date = updates.dateOfBirth.split('T')[0];
        } else {
          apiUpdates.birth_date = updates.dateOfBirth;
        }
      } else {
        // Empty or null - clear the field
        apiUpdates.birth_date = null;
      }
    }

    // Gender and marital status (SSOT from /choices/ endpoints)
    if (updates.gender !== undefined) apiUpdates.gender = updates.gender;
    if (updates.maritalStatus !== undefined) apiUpdates.marital_status = updates.maritalStatus;

    // Group leader flag
    if (updates.isGroupLeader !== undefined) apiUpdates.is_group_leader = updates.isGroupLeader;

    // Resignation fields
    if (updates.resignationDate !== undefined) apiUpdates.resignation_date = updates.resignationDate;
    if (updates.resignationReason !== undefined) apiUpdates.resignation_reason = updates.resignationReason;

    // Address fields - backend uses flat fields (address_line1, address_line2, city, state, postal_code)
    // Support both SSOT flat fields (preferred) and legacy nested address object

    // SSOT flat address fields (preferred - used by ContactInfoTab)
    if (updates.addressLine1 !== undefined) apiUpdates.address_line1 = updates.addressLine1;
    if (updates.addressLine2 !== undefined) apiUpdates.address_line2 = updates.addressLine2;
    if (updates.city !== undefined) apiUpdates.city = updates.city;
    if (updates.state !== undefined) apiUpdates.state = updates.state;
    if (updates.postalCode !== undefined) apiUpdates.postal_code = updates.postalCode;

    // Legacy nested address object (backwards compatibility)
    if (updates.address !== undefined) {
      // Handle both street and street1 field names from frontend
      const street = updates.address.street || updates.address.street1;
      if (street !== undefined) apiUpdates.address_line1 = street;
      if (updates.address.street2 !== undefined) apiUpdates.address_line2 = updates.address.street2;
      if (updates.address.city !== undefined) apiUpdates.city = updates.address.city;
      if (updates.address.state !== undefined) apiUpdates.state = updates.address.state;
      // Handle both zipCode and postalCode field names from frontend
      const postalCode = updates.address.zipCode || updates.address.postalCode;
      if (postalCode !== undefined) apiUpdates.postal_code = postalCode;
    }

    console.log('[ApiMemberService] updateMember - sending PATCH with only changed fields:', Object.keys(apiUpdates));

    // Use PATCH for partial updates - this only updates the fields provided
    // PUT would replace the entire resource which is NOT what we want
    const response = await fetch(`/api/v1/members/${id}/`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(apiUpdates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.detail || 'Failed to update member');
    }

    // Fetch updated member to get full details
    return this.getMemberById(id);
  }

  async deleteMember(id: string): Promise<void> {
    // Get church ID from current user
    const user = authStateManager.getUser();
    const churchId = user?.churchId || user?.church_id;
    if (!churchId) {
      throw new Error('User must be associated with a church to delete members');
    }
    
    const response = await fetch(`/api/v1/churches/${churchId}/members/${id}/`, {
      method: 'DELETE',
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete member: ${response.statusText}`);
    }
  }

  // Group Operations
  async getGroups(filter?: {
    search?: string;
    type?: Group['type'][];
    status?: Group['status'];
  }): Promise<Group[]> {
    const params = new URLSearchParams();

    if (filter) {
      if (filter.search) params.append('search', filter.search);
      if (filter.type?.length) {
        // Map frontend types to backend group_type values
        filter.type.forEach(type => params.append('group_type', type));
      }
      if (filter.status) {
        params.append('is_active', filter.status === 'active' ? 'true' : 'false');
      }
    }

    try {
      // Get church ID from current user
      const user = authStateManager.getUser();
      if (!user?.churchId) {
        const errorDetails = {
          message: 'User must be associated with a church to view groups',
          component: 'ApiMemberService',
          method: 'getGroups',
          user: user?.email || 'unknown',
          timestamp: new Date().toISOString(),
          requiresAuth: true
        };
        console.error('[ApiMemberService] Missing church context:', errorDetails);
        throw new Error(JSON.stringify(errorDetails, null, 2));
      }

      const response = await fetch(`/api/v1/churches/${user.churchId}/groups/?${params}`, {
        headers: await this.getHeaders()
      });

      if (!response.ok) {
        const errorDetails = {
          message: 'Failed to fetch groups',
          component: 'ApiMemberService',
          method: 'getGroups',
          endpoint: `/api/v1/churches/${user.churchId}/groups/`,
          churchId: user.churchId,
          user: user.email,
          status: response.status,
          statusText: response.statusText,
          timestamp: new Date().toISOString()
        };
        console.error('[ApiMemberService] API Error:', errorDetails);
        throw new Error(JSON.stringify(errorDetails, null, 2));
      }

      const data = await response.json();
      const groups = data.results || data || [];

      // Transform backend Group objects to frontend Group objects
      return groups.map((group: any) => ({
        id: group.id,
        name: group.name,
        description: group.description,
        type: group.group_type as any,
        status: group.is_active ? 'active' as const : 'inactive' as const,
        leaderId: group.leader || '',
        currentSize: group.member_count || 0,
        memberIds: [],
        maxSize: group.max_capacity,
        isPublic: true,
        allowSelfSignup: false,
        requireApproval: true,
        meetingDay: group.meeting_day,
        meetingTime: group.meeting_time,
        meetingLocation: group.meeting_location,
        startDate: group.created_at,
        endDate: null,
        tags: [],
        visibility: 'members' as const,
        color: '#3B82F6'
      }));
    } catch (error: any) {
      // Re-throw if already a formatted error
      if (error.message?.startsWith('{')) {
        throw error;
      }

      // Format unexpected errors
      const errorDetails = {
        message: 'Unexpected error fetching groups',
        component: 'ApiMemberService',
        method: 'getGroups',
        originalError: error.message || String(error),
        stack: error.stack,
        timestamp: new Date().toISOString()
      };
      console.error('[ApiMemberService] Unexpected Error:', errorDetails);
      throw new Error(JSON.stringify(errorDetails, null, 2));
    }
  }

  async getGroupById(id: string): Promise<Group> {
    // Get church ID from current user
    const user = authStateManager.getUser();
    if (!user?.churchId) {
      throw new Error('User must be associated with a church to view group');
    }

    const response = await fetch(`/api/v1/churches/${user.churchId}/groups/${id}/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch group: ${response.statusText}`);
    }

    const group = await response.json();

    // Transform backend Group to frontend format
    return {
      id: group.id,
      name: group.name,
      description: group.description,
      type: group.group_type as any,
      status: group.is_active ? 'active' as const : 'inactive' as const,
      leaderId: group.leader || '',
      currentSize: group.member_count || 0,
      memberIds: [],
      maxSize: group.max_capacity,
      isPublic: true,
      allowSelfSignup: false,
      requireApproval: true,
      meetingDay: group.meeting_day,
      meetingTime: group.meeting_time,
      meetingLocation: group.meeting_location,
      startDate: group.created_at,
      endDate: null,
      tags: [],
      visibility: 'members' as const,
      color: '#3B82F6'
    };
  }

  async createGroup(data: Omit<Group, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'currentSize'> & { ministryId?: string }): Promise<Group> {
    // Get church ID from current user
    const user = authStateManager.getUser();
    if (!user?.churchId) {
      throw new Error('User must be associated with a church to create groups');
    }

    // Ministry is now optional - use the one from form data if provided
    // Groups can exist without a ministry (standalone groups)
    const ministryId = (data as any).ministryId || null;

    // Transform Group data to backend Group format
    const groupData: Record<string, any> = {
      name: data.name,
      description: data.description || '',
      group_type: data.type || 'small_group',
      meeting_day: data.meetingDay || null,
      meeting_time: data.meetingTime || null,
      meeting_location: data.meetingLocation || null,
      leader: data.leaderId || null,
      max_capacity: data.maxSize || null,
      is_active: data.status === 'active'
    };

    // Only include ministry if one was selected
    if (ministryId) {
      groupData.ministry = ministryId;
    }

    const response = await fetch(`/api/v1/churches/${user.churchId}/groups/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(groupData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create group');
    }

    const group = await response.json();

    // Transform backend Group response to frontend Group format
    return {
      id: group.id,
      name: group.name,
      description: group.description,
      type: group.group_type as any,
      status: group.is_active ? 'active' as const : 'inactive' as const,
      leaderId: group.leader || '',
      currentSize: group.member_count || 0,
      memberIds: [],
      maxSize: group.max_capacity,
      isPublic: true,
      allowSelfSignup: false,
      requireApproval: true,
      meetingDay: group.meeting_day,
      meetingTime: group.meeting_time,
      meetingLocation: group.meeting_location,
      startDate: group.created_at,
      endDate: null,
      tags: [],
      visibility: 'members' as const,
      color: '#3B82F6'
    };
  }

  async updateGroup(id: string, updates: Partial<Group>): Promise<Group> {
    // Get church ID from current user
    const user = authStateManager.getUser();
    if (!user?.churchId) {
      throw new Error('User must be associated with a church to update groups');
    }

    // Transform frontend Group updates to backend format
    const groupUpdates: any = {};
    if (updates.name) groupUpdates.name = updates.name;
    if (updates.description !== undefined) groupUpdates.description = updates.description;
    if (updates.status) groupUpdates.is_active = updates.status === 'active';
    if (updates.type) groupUpdates.group_type = updates.type;
    if (updates.leaderId !== undefined) groupUpdates.leader = updates.leaderId || null;
    if (updates.maxSize !== undefined) groupUpdates.max_capacity = updates.maxSize;
    if (updates.meetingDay !== undefined) groupUpdates.meeting_day = updates.meetingDay;
    if (updates.meetingTime !== undefined) groupUpdates.meeting_time = updates.meetingTime;
    if (updates.meetingLocation !== undefined) groupUpdates.meeting_location = updates.meetingLocation;

    const response = await fetch(`/api/v1/churches/${user.churchId}/groups/${id}/`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(groupUpdates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update group');
    }

    const group = await response.json();

    // Transform backend Group response to frontend format
    return {
      id: group.id,
      name: group.name,
      description: group.description,
      type: group.group_type as any,
      status: group.is_active ? 'active' as const : 'inactive' as const,
      leaderId: group.leader || '',
      currentSize: group.member_count || 0,
      memberIds: [],
      maxSize: group.max_capacity,
      isPublic: true,
      allowSelfSignup: false,
      requireApproval: true,
      meetingDay: group.meeting_day,
      meetingTime: group.meeting_time,
      meetingLocation: group.meeting_location,
      startDate: group.created_at,
      endDate: null,
      tags: [],
      visibility: 'members' as const,
      color: '#3B82F6'
    };
  }

  async deleteGroup(id: string): Promise<void> {
    // Get church ID from current user
    const user = authStateManager.getUser();
    if (!user?.churchId) {
      throw new Error('User must be associated with a church to delete groups');
    }

    const response = await fetch(`/api/v1/churches/${user.churchId}/groups/${id}/`, {
      method: 'DELETE',
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete group: ${response.statusText}`);
    }
  }

  // Group Membership Operations
  async addMemberToGroup(memberId: string, groupId: string): Promise<void> {
    // Get church ID from current user
    const user = authStateManager.getUser();
    if (!user?.churchId) {
      throw new Error('User must be associated with a church to manage group membership');
    }

    const response = await fetch(`/api/v1/churches/${user.churchId}/groups/${groupId}/add-members/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ member_ids: [memberId] })
    });

    if (!response.ok) {
      throw new Error(`Failed to add member to group: ${response.statusText}`);
    }
  }

  async removeMemberFromGroup(memberId: string, groupId: string): Promise<void> {
    // Get church ID from current user
    const user = authStateManager.getUser();
    if (!user?.churchId) {
      throw new Error('User must be associated with a church to manage group membership');
    }

    const response = await fetch(`/api/v1/churches/${user.churchId}/groups/${groupId}/remove-member/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ member_id: memberId })
    });

    if (!response.ok) {
      throw new Error(`Failed to remove member from group: ${response.statusText}`);
    }
  }

  async getGroupMembers(groupId: string): Promise<Member[]> {
    const response = await fetch(`/api/v1/churches/groups/${groupId}/members/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch group members: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  async getMemberGroups(memberId: string): Promise<Group[]> {
    const response = await fetch(`/api/v1/churches/members/${memberId}/groups/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch member groups: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  // Family Operations
  async getFamilies(): Promise<Family[]> {
    const response = await fetch(`/api/v1/churches/families/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch families: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  async getFamilyById(id: string): Promise<Family> {
    const response = await fetch(`/api/v1/churches/families/${id}/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch family: ${response.statusText}`);
    }

    return response.json();
  }

  async createFamily(data: Omit<Family, 'id' | 'createdAt' | 'updatedAt'>): Promise<Family> {
    const response = await fetch(`/api/v1/churches/families/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create family');
    }

    return response.json();
  }

  async updateFamily(id: string, updates: Partial<Family>): Promise<Family> {
    const response = await fetch(`/api/v1/churches/families/${id}/`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update family');
    }

    return response.json();
  }

  async addMemberToFamily(memberId: string, familyId: string, role: string): Promise<void> {
    const response = await fetch(`/api/v1/churches/families/${familyId}/add_member/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ member_id: memberId, role })
    });

    if (!response.ok) {
      throw new Error(`Failed to add member to family: ${response.statusText}`);
    }
  }

  async removeMemberFromFamily(memberId: string, familyId: string): Promise<void> {
    const response = await fetch(`/api/v1/churches/families/${familyId}/remove_member/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ member_id: memberId })
    });

    if (!response.ok) {
      throw new Error(`Failed to remove member from family: ${response.statusText}`);
    }
  }

  // Bulk Operations
  async bulkImportMembers(data: MemberImportData[]): Promise<{
    imported: number;
    failed: number;
    errors: Array<{row: number; error: string}>;
  }> {
    const response = await fetch(`/api/v1/churches/members/bulk_import/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ members: data })
    });

    if (!response.ok) {
      throw new Error(`Failed to import members: ${response.statusText}`);
    }

    return response.json();
  }

  async bulkExportMembers(filter?: MemberFilter, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    
    if (filter) {
      if (filter.search) params.append('search', filter.search);
      if (filter.status) params.append('status', filter.status);
      if (filter.groupIds?.length) params.append('groups', filter.groupIds.join(','));
    }

    const response = await fetch(`/api/v1/churches/members/export?${params}`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to export members: ${response.statusText}`);
    }

    return response.blob();
  }

  async bulkOperation(operation: BulkOperation): Promise<{
    succeeded: number;
    failed: number;
    errors: string[];
  }> {
    const response = await fetch(`/api/v1/churches/members/bulk_operation/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(operation)
    });

    if (!response.ok) {
      throw new Error(`Failed to perform bulk operation: ${response.statusText}`);
    }

    return response.json();
  }

  // Activity and Analytics
  async getMemberActivity(memberId: string, dateRange?: {start: string; end: string}): Promise<MemberActivity[]> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('start_date', dateRange.start);
      params.append('end_date', dateRange.end);
    }

    const response = await fetch(`/api/v1/churches/members/${memberId}/activities?${params}`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch member activity: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  async recordActivity(activity: Omit<MemberActivity, 'id'>): Promise<MemberActivity> {
    const response = await fetch(`/api/v1/churches/activities/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(activity)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to record activity');
    }

    return response.json();
  }

  async getMemberStats(): Promise<MemberStats> {
    const response = await fetch(`/api/v1/churches/members/stats/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch member stats: ${response.statusText}`);
    }

    return response.json();
  }

  // Search and Filtering
  async searchMembers(query: string): Promise<Member[]> {
    const response = await fetch(`/api/v1/churches/members/search?q=${encodeURIComponent(query)}`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to search members: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  async getMembersByBirthMonth(month: number): Promise<Member[]> {
    const response = await fetch(`/api/v1/churches/members/birthdays?month=${month}`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch birthday members: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  async getMembersByAnniversaryMonth(month: number): Promise<Member[]> {
    const response = await fetch(`/api/v1/churches/members/anniversaries?month=${month}`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch anniversary members: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  // Communication
  async getMemberCommunicationHistory(memberId: string): Promise<Array<{
    id: string;
    type: 'email' | 'sms' | 'phone' | 'mail';
    date: string;
    subject?: string;
    status: 'sent' | 'delivered' | 'opened' | 'clicked' | 'failed';
  }>> {
    const response = await fetch(`/api/v1/churches/members/${memberId}/communications/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch communication history: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  async updateCommunicationPreferences(memberId: string, preferences: Member['communicationPreferences']): Promise<void> {
    const response = await fetch(`/api/v1/churches/members/${memberId}/communication_preferences/`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(preferences)
    });

    if (!response.ok) {
      throw new Error(`Failed to update communication preferences: ${response.statusText}`);
    }
  }

  // Directory
  async getDirectoryMembers(): Promise<Array<Pick<Member, 'id' | 'firstName' | 'lastName' | 'email' | 'phone' | 'address'>>> {
    const response = await fetch(`/api/v1/churches/directory/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch directory: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  // Profile Picture Operations
  async uploadProfilePicture(memberId: string, file: File): Promise<{url: string}> {
    const formData = new FormData();
    formData.append('profile_picture', file);

    const token = await tokenManager.getToken();
    const user = authStateManager.getUser();
    const selectedChurchId = user?.churchId || user?.church_id ||
                             user?.preferences?.selectedChurchId ||
                             localStorage.getItem('selectedChurchId');

    const response = await fetch(`/api/v1/members/${memberId}/upload_picture/`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(selectedChurchId ? { 'X-Selected-Church-ID': selectedChurchId } : {})
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to upload profile picture: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteProfilePicture(memberId: string): Promise<void> {
    const response = await fetch(`/api/v1/members/${memberId}/delete_picture/`, {
      method: 'DELETE',
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete profile picture: ${response.statusText}`);
    }
  }

  // Tags and Custom Fields
  async getAvailableTags(): Promise<string[]> {
    const response = await fetch(`/api/v1/churches/tags/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tags: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  async addTag(memberId: string, tag: string): Promise<void> {
    const response = await fetch(`/api/v1/churches/members/${memberId}/add_tag/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ tag })
    });

    if (!response.ok) {
      throw new Error(`Failed to add tag: ${response.statusText}`);
    }
  }

  async removeTag(memberId: string, tag: string): Promise<void> {
    const response = await fetch(`/api/v1/churches/members/${memberId}/remove_tag/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ tag })
    });

    if (!response.ok) {
      throw new Error(`Failed to remove tag: ${response.statusText}`);
    }
  }

  async getCustomFieldDefinitions(): Promise<Array<{
    key: string;
    label: string;
    type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean';
    options?: string[];
    required?: boolean;
  }>> {
    const response = await fetch(`/api/v1/churches/custom_fields/`, {
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch custom fields: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results || data || [];
  }

  // Helper method to convert Group[] to MemberGroup[] for the filter component
  async getGroupsForFilter(): Promise<MemberGroup[]> {
    const groups = await this.getGroups();
    return groups.map(g => ({
      id: g.id,
      name: g.name,
      memberCount: g.currentSize,
      type: g.type,
      description: g.description
    }));
  }

  // PIN Management (Admin)
  async resetMemberPIN(memberId: string, newPin: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`/api/v1/members/${memberId}/reset_pin/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ new_pin: newPin })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to reset PIN');
    }

    return response.json();
  }

  async clearMemberPIN(memberId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`/api/v1/members/${memberId}/clear_pin/`, {
      method: 'POST',
      headers: await this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to clear PIN');
    }

    return response.json();
  }
}